import { createSelector } from 'reselect';
import * as utils from '../helpers/utils';
import * as eventHelper from '../helpers/event-helper';
import { RootState } from './reducers';
import { Contact, SlotEvent, RampService, User } from '../model/types';

/*
  Airports
 */

export const getAirports = (state: RootState) => state.airports.list;

/*
  Contacts
 */
export const getContacts = (state: RootState): Contact[] => state.contacts.list;

export const getSortedContacts = createSelector(
  getContacts,
  (contacts: Contact[]) =>
    [...contacts].sort((contact1, contact2) => {
      const name1 = contact1.name.toUpperCase();
      const name2 = contact2.name.toUpperCase();
      if (name1 < name2) return -1;
      if (name1 > name2) return 1;
      return 0;
    })
);

/*
  Events
 */
export const getEvents = (state: RootState): SlotEvent[] => state.events.list;

export const getEventsById = (state: RootState): Map<string, SlotEvent> =>
  state.events.byId;

export const getEventById = (
  state: RootState,
  eventId: string
): SlotEvent | null => getEventsById(state).get(eventId) || null;

export const getRange = (state: RootState) => state.events.range;

export const getFilteredEvents = createSelector(
  [getEvents, getAirports],
  (events, airports) => {
    const apNames = airports.filter(ap => ap.selected).map(ap => ap.name);
    return apNames.length
      ? events.filter((e: SlotEvent) =>
          apNames.includes(e.airport.toUpperCase())
        )
      : events;
  }
);

/*
  Ramp Services
 */
export const getRampServices = (state: RootState): RampService[] =>
  state.rampServices.list;

export const getSortedRampServices = createSelector(
  [getRampServices],
  (rampServices: RampService[]) =>
    [...rampServices].sort(utils.makeCompareStrings('name'))
);

/*
  Statistics
 */

export const getStatistics = (state: RootState): SlotEvent[] =>
  state.statistics.list;

export const getSortedStatistics = createSelector(
  [getStatistics],
  (events: SlotEvent[]) => eventHelper.sortEvents(events)
);

export const getStatisticsFilters = (
  state: RootState
): {
  airports: string[];
  inboundAirports: string[];
  outboundAirports: string[];
  airlines: string[];
} => state.statistics.filters;

export const getFilteredStatistics = createSelector(
  [getSortedStatistics, getStatisticsFilters],
  (
    events: SlotEvent[],
    filters: {
      airports: string[];
      inboundAirports: string[];
      outboundAirports: string[];
      airlines: string[];
    }
  ) =>
    events.filter(
      (event: SlotEvent) =>
        (!filters.airports.length ||
          filters.airports.includes(event.airport)) &&
        (!filters.inboundAirports.length ||
          filters.inboundAirports.includes(event.inboundAirport)) &&
        (!filters.outboundAirports.length ||
          filters.outboundAirports.includes(event.outboundAirport)) &&
        (!filters.airlines.length || filters.airlines.includes(event.airline))
    )
);

/*
  Users
*/

export const getUsers = (state: RootState): User[] => state.users.list;

export const getUserById = (
  state: RootState,
  userId: string
): User | undefined => state.users.byId[userId];

/*
  Notification
 */

export const getNotification = (
  state: RootState
): {
  message?: string;
  severity?: 'success' | 'error' | 'warning' | 'info';
  top?: number;
} => state.notification;

/*
  Loading - Overlay (blocking modal)
 */

export const showLoadingOverlay = (state: RootState): boolean =>
  state.loading.overlay.isVisible;

export const getLoadingOverlayMessage = (
  state: RootState
): string | undefined => state.loading.overlay.message;

/*
  Loading - Activity (non-blocking indicator)
 */

export const showActivityIndicator = (state: RootState): boolean =>
  state.loading.activity.count > 0;

/*
  Network Status
 */

export const getNetworkStatus = (state: RootState) => state.loading.networkStatus;
